import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { getUserData } from "./models/user";
import { getMapObjects } from "./models/mapObject";
import {
  getShopItemsShopItemsGet,
  getTop100Top100Get,
  pingPingGet,
} from "./client";
import { prefetchAllQuests } from "./utils/quest/questsCache";
import { preloadLastHouse, preloadMap } from "./scenes/map/mapPreload";
import useMapViewStore from "./stores/useMapViewStore";
import { fetchOnboardingStateFromServer } from "./utils/net/onboardingApi";
// CloudStorageSync removed - using direct API calls instead
import { LifeGame } from "./LifeGame";
import FullscreenContainer from "./components/FullscreenContainer";
import ModalFrame from "./ui/primitives/ModalFrame";
import { createPortal } from "react-dom";
import posthog from "posthog-js";
import { splashImage } from "./ui/seasonalAssets";


const backgroundImage = splashImage();

/** After the minimum splash time, how long to keep waiting for the map to finish drawing. */
const MAP_READY_WAIT_MS = 6000;

/** Resolves when the map is fully drawn under the splash (or after `timeoutMs`). */
function mapSceneBuilt(timeoutMs: number): Promise<void> {
    if (useMapViewStore.getState().mapSceneBuilt) return Promise.resolve();
    return new Promise((resolve) => {
        const done = () => { unsubscribe(); clearTimeout(timer); resolve(); };
        const unsubscribe = useMapViewStore.subscribe((s) => { if (s.mapSceneBuilt) done(); });
        const timer = setTimeout(done, timeoutMs);
    });
}

/**
 * `onConnected` — the player's data is in: mount the game *under* the splash, so
 * the map is built while the splash still shows. `onSuccess` — the splash may go:
 * its minimum time is over and, for a player heading to the map, the map is fully
 * drawn (or MAP_READY_WAIT_MS ran out), so it appears whole instead of filling in.
 */
export const ServerConnectGate = ({ onConnected, onSuccess }: { onConnected?: () => void; onSuccess: () => void }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const phrases = useMemo(() => [
        t("The sweeping of sand off rooftops..."),
        t("The whispering to the old palm trees..."),
        t("The brushing of seashells from the welcome mat..."),
        t("The inviting of the wind to tell its story..."),
        t("The counting of lanterns in the harbor mist..."),
        t("The humming of ancient island melodies..."),
        t("The waking of the stone guardians..."),
        t("The filling of the air with salt and sun..."),
        t("The untangling of fishing nets of dreams..."),
        t("The waiting for the dolphins to return..."),
        t("The drawing of new coastlines in the sand..."),
        t("The listening to waves reciting poetry..."),
        t("The lighting of bonfires for travelers..."),
        t("The calling of the spirits of the tide..."),
        t("The rinsing off of the footprints of yesterday..."),
        t("The stirring of tea leaves for omens..."),
        t("The chasing of fireflies by the lagoon..."),
        t("The sharpening of the oars for new journeys..."),
        t("The turning of the stars above your island..."),
        t("The smoothing of the paths to forgotten doors...")
    ], [t]);

    const [phrase, setPhrase] = useState(() => getRandomPhrase(phrases));
    const [emoji, setEmoji] = useState("ᕙ(`▽´)ᕗ");
    const [showErrorModal, setShowErrorModal] = useState(false);

    const phraseIndex = useRef(0);
    const phraseInterval = useRef<number | null>(null);
    const initCalledRef = useRef(false);
    const isMountedRef = useRef(true);
    const failedQueryTypesRef = useRef<string[]>([]);

    const nextPhrase = useCallback(() => {
        phraseIndex.current = (phraseIndex.current + 1) % phrases.length;
        setPhrase(phrases[phraseIndex.current]);
        setEmoji("ᕙ(`▽´)ᕗ");
    }, [phrases]);

    const executeQuery = useCallback(async (type: string): Promise<boolean> => {
        try {
            switch (type) {
                case "userData":
                    await queryClient.prefetchQuery({ queryKey: ["userData"], queryFn: getUserData });
                    break;
                case "onboardingState":
                    await queryClient.prefetchQuery({ 
                        queryKey: ["onboarding-state"], 
                        queryFn: fetchOnboardingStateFromServer,
                        staleTime: 5 * 60 * 1000 // 5 minutes
                    });
                    break;
            }
            return true;
        } catch {
            return false;
        }
    }, [queryClient]);

    const warmUpCache = useCallback(async (retryOnlyFailed = false): Promise<string[] | null> => {
        // Прогріваємо кеш під час завантаження
        // Виконуємо запити паралельно для швидшої завантаження
        // КРИТИЧНО ВАЖЛИВІ ДАНІ: userData та onboardingState - мають бути завантажені ПЕРЕД переходом до гри
        // Прибрано: top100, shopItems, mapObjects, quests, dailyRewardsStatus - завантажуються при відкритті відповідних екранів
        const MAX_RETRIES = 3; // Максимум 3 додаткові спроби (перша спроба враховується окремо)
        const allQueryTypes = ["userData", "onboardingState"];
        let failedTypes: string[] = retryOnlyFailed ? [...failedQueryTypesRef.current] : [];

        // Якщо не retry - виконуємо всі запити паралельно
        if (!retryOnlyFailed) {
            const firstAttemptPromises = [
                queryClient.prefetchQuery({ queryKey: ["userData"], queryFn: getUserData }).catch(() => null),
                queryClient.prefetchQuery({ 
                    queryKey: ["onboarding-state"], 
                    queryFn: fetchOnboardingStateFromServer,
                    staleTime: 5 * 60 * 1000 
                }).catch(() => null),
            ];

            const results = await Promise.allSettled(firstAttemptPromises);
            
            // Знаходимо невдалі запити
            results.forEach((result, index) => {
                if (result.status === "rejected" || result.value === null) {
                    failedTypes.push(allQueryTypes[index]);
                }
            });
        }

        // Якщо є невдалі запити - повторюємо тільки їх
        if (failedTypes.length > 0) {
            for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
                if (!isMountedRef.current) break;
                
                const retryPromises = failedTypes.map(async (type) => {
                    const success = await executeQuery(type);
                    return { type, success };
                });

                const retryResults = await Promise.all(retryPromises);
                const stillFailed = retryResults
                    .filter((r) => !r.success)
                    .map((r) => r.type);

                if (stillFailed.length === 0) {
                    // Всі запити успішні
                    failedQueryTypesRef.current = [];
                    return null;
                }

                if (attempt < MAX_RETRIES) {
                    // Чекаємо перед наступною спробою
                    await new Promise((resolve) => setTimeout(resolve, 3000));
                    failedTypes = stillFailed;
                } else {
                    // Вичерпано спроби - повертаємо список невдалих
                    failedQueryTypesRef.current = stillFailed;
                    return stillFailed;
                }
            }
        }

        failedQueryTypesRef.current = [];
        return null;
    }, [executeQuery, queryClient]);

    const tryConnect = useCallback(async () => {
        if (!isMountedRef.current) return;
        
        try {
            await pingPingGet({ throwOnError: true });
            
            // Після успішного підключення завантажуємо КРИТИЧНО ВАЖЛИВІ дані БЛОКУЮЧЕ
            const showDuration = 4000; // 4 секунди мінімум
            const startTime = Date.now();
            
            // БЛОКУЮЧЕ завантаження критично важливих даних
            const criticalDataFailed = await warmUpCache();
            
            if (criticalDataFailed && criticalDataFailed.length > 0) {
                console.error(`[ServerConnectGate] Failed to load critical data:`, criticalDataFailed);
                // Якщо критично важливі дані не завантажилися - показуємо помилку
                if (isMountedRef.current) {
                    setShowErrorModal(true);
                }
                return;
            }
            
            // Heading for the map (onboarding done): start loading its pictures and
            // the last visited house now, while the splash is still up. In the
            // background — the splash never waits for them.
            const onboarding = queryClient.getQueryData<{ onboarding_completed?: boolean }>(["onboarding-state"]);
            if (onboarding?.onboarding_completed) {
                preloadMap(queryClient);
                preloadLastHouse(queryClient);
            }

            // Починаємо фонове завантаження додаткових даних (не блокує перехід)
            Promise.allSettled([
                prefetchAllQuests(queryClient).catch((error) => {
                    console.error("[ServerConnectGate] Failed to load quests in background:", error);
                }),
                queryClient.prefetchQuery({ queryKey: ['mapObjects'], queryFn: getMapObjects }).catch((error) => {
                    console.error("[ServerConnectGate] Failed to load mapObjects in background:", error);
                }),
                queryClient.prefetchQuery({ queryKey: ["top100"], queryFn: async () => {
                  const { data } = await getTop100Top100Get({ throwOnError: true });
                  return data;
                } }).catch((error) => {
                    console.error("[ServerConnectGate] Failed to load top100 in background:", error);
                }),
                queryClient.prefetchQuery({ queryKey: ["shopItems"], queryFn: async () => {
                  const { data } = await getShopItemsShopItemsGet({ throwOnError: true });
                  return data;
                } }).catch((error) => {
                    console.error("[ServerConnectGate] Failed to load shopItems in background:", error);
                })
            ]).catch((error) => {
                console.error("[ServerConnectGate] Background loading error:", error);
            });
            
            // The game mounts under the splash now.
            if (isMountedRef.current) onConnected?.();

            // Чекаємо мінімум 4 секунди перед переходом (після завантаження критичних даних)
            const elapsed = Date.now() - startTime;
            const remainingTime = Math.max(0, showDuration - elapsed);
            
            if (remainingTime > 0) {
                await new Promise((resolve) => setTimeout(resolve, remainingTime));
            }

            // Heading for the map: let it finish drawing behind the splash.
            if (onboarding?.onboarding_completed) await mapSceneBuilt(MAP_READY_WAIT_MS);
            
            if (isMountedRef.current) {
                try {
                    posthog.capture("onboarding_server_gate_passed");
                } catch {
                    /* never block transition */
                }
                onSuccess();
            }
        } catch (e) {
            // Глобальний Sentry автоматично ловить помилки
            // fetchWithRetryForDirectCalls вже зробив всі спроби (1 retry = 2 спроби загалом)
            // Якщо дійшли сюди - показуємо попап одразу
            console.warn(`[ServerConnectGate] Ping failed after all retries:`, e);
            
            if (!isMountedRef.current) return;
            
            // Показуємо попап одразу (без циклу retry)
            if (isMountedRef.current) {
                setShowErrorModal(true);
            }
        }
    }, [onConnected, onSuccess, queryClient, warmUpCache]);

    const handleRetry = async () => {
        if (!isMountedRef.current) return;
        
        setShowErrorModal(false);
        
        // Повторюємо весь процес (ping + warmUpCache)
        tryConnect();
    };

    useEffect(() => {
        isMountedRef.current = true;
        
        // Викликаємо tryConnect один раз при монтуванні
        if (!initCalledRef.current) {
            initCalledRef.current = true;
            tryConnect();
        }
        
        // Змінюємо цитати кожну секунду
        phraseInterval.current = window.setInterval(() => {
            nextPhrase();
        }, 1000);

        return () => {
            isMountedRef.current = false;
            if (phraseInterval.current !== null) {
                clearInterval(phraseInterval.current);
            }
        };
    }, [nextPhrase, tryConnect]);

    return (
        <FullscreenContainer className="loto-font">
            <style>{`
        .background-wrapper {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
        }

        .background-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center top;
          z-index: 0;
        }

        .foreground-content {
          position: relative;
          z-index: 1;
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-end;
          box-sizing: border-box;
          padding: 20px;
          gap: 12px;
        }

        .content-wrapper {
          width: 100%;
          max-width: 300px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .life-game-wrapper {
        }


        .phrase {
          color: var(--color-white);
          font-size: 18px;
          font-weight: 700;
          text-align: center;
          line-height: 1.1;
          word-break: break-word;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .emoji {
          color: var(--color-white);
          font-size: 22px;
          font-weight: 700;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>

            <div className="background-wrapper">
                <img
                    src={backgroundImage}
                    alt={t("Island Harbor")}
                    className="background-img"
                    draggable={false}
                />

                <div className="foreground-content">
                    <div className="content-wrapper">
                        <div className="emoji">{emoji}</div>
                        <div className="phrase">{phrase}</div>
                    </div>

                    <div className="life-game-wrapper">
                        <LifeGame />
                    </div>
                </div>
            </div>
            
            {showErrorModal && createPortal(
                <ModalFrame
                    isOpen={true}
                    onClose={() => {}} // Не дозволяємо закрити без дії
                    title={t("Connection failed")}
                    backdropClassName="modal-frame-bottom-gradient [--modal-frame-gradient-max-width:520px]"
                >
                    <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '20px',
                        textAlign: 'center',
                        padding: '20px 0'
                    }}>
                        <p style={{ 
                            fontSize: '16px', 
                            lineHeight: '1.5',
                            margin: 0,
                            color: 'var(--color-white)'
                        }}>
                            {t("We sincerely apologize. The server may be undergoing maintenance. You can try again or come back later")}
                        </p>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            alignItems: 'center'
                        }}>
                            <button
                                onClick={handleRetry}
                                style={{
                                    display: 'flex',
                                    padding: '12px 48px',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '6px',
                                    borderRadius: 100,
                                    border: 'none',
                                    background: 'var(--gradient-action-primary)',
                                    color: 'var(--color-white)',
                                    fontFamily: '"Rubik One", sans-serif',
                                    fontSize: '16px',
                                    fontWeight: 400,
                                    lineHeight: '24px',
                                    cursor: 'pointer',
                                    boxShadow: `-3px 4px 7.1px 0 var(--color-black-alpha-25)`,
                                    width: 'fit-content',
                                }}
                            >
                                {t("I want to try again")}
                            </button>
                            <a
                                href="https://t.me/web3islands_contact"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    color: 'var(--color-white-alpha-60)',
                                    fontSize: '13px',
                                    textDecoration: 'underline',
                                }}
                            >
                                {t("Contact support")}
                            </a>
                        </div>
                    </div>
                </ModalFrame>,
                document.body
            )}
        </FullscreenContainer>
    );
};

function getRandomPhrase(phrases: string[], current?: string): string {
    let phrase;
    do {
        phrase = phrases[Math.floor(Math.random() * phrases.length)];
    } while (phrase === current && phrases.length > 1);
    return phrase;
}
